import { NovuProvider } from '@novu/react';
import { type ComponentProps, createContext, type ReactNode, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { SOCKET_OPTIONS } from '@/config';
import { useAuth } from '@/context/auth/hooks';
import { useEnvironment } from '@/context/environment/hooks';
import { useDashboardInboxHashes } from '@/hooks/use-dashboard-inbox-hashes';
import { apiHostnameManager } from '@/utils/api-hostname-manager';
import { createContextHook } from '@/utils/context';

type ConnectSubscriberContextValue = {
  subscriberId: string;
  isReady: boolean;
};

const ConnectSubscriberContext = createContext<ConnectSubscriberContextValue>({
  subscriberId: '',
  isReady: false,
});

export const useConnectSubscriber = createContextHook(ConnectSubscriberContext);

type ConnectSubscriberProviderProps = {
  children?: ReactNode;
};

type NovuProviderChildren = ComponentProps<typeof NovuProvider>['children'];

export function ConnectSubscriberProvider({ children }: ConnectSubscriberProviderProps) {
  const { currentUser, isUserLoaded } = useAuth();
  const { currentEnvironment } = useEnvironment();
  const { subscriberHash, isSettled: isHashSettled } = useDashboardInboxHashes();

  const isReady = isUserLoaded && !!currentUser?._id && !!currentEnvironment?.identifier;
  const subscriberId = currentUser?._id || '';
  const routedContent = (children === undefined ? <Outlet /> : children) as NovuProviderChildren;
  const subscriber = useMemo(
    () => ({
      subscriberId,
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      email: currentUser?.email ?? '',
      avatar: currentUser?.profilePicture ?? '',
    }),
    [subscriberId, currentUser?.firstName, currentUser?.lastName, currentUser?.email, currentUser?.profilePicture]
  );

  if (!isReady) {
    return (
      <ConnectSubscriberContext.Provider value={{ subscriberId, isReady: false }}>
        {routedContent}
      </ConnectSubscriberContext.Provider>
    );
  }

  // Wrapping the page in NovuProvider later would remount it, so it waits for the hash unrendered.
  if (!isHashSettled) {
    return null;
  }

  return (
    <ConnectSubscriberContext.Provider value={{ subscriberId, isReady: true }}>
      <NovuProvider
        key={subscriberId}
        subscriber={subscriber}
        subscriberHash={subscriberHash}
        applicationIdentifier={currentEnvironment.identifier}
        apiUrl={apiHostnameManager.getHostname()}
        socketUrl={apiHostnameManager.getWebSocketHostname()}
        socketOptions={SOCKET_OPTIONS}
      >
        {routedContent}
      </NovuProvider>
    </ConnectSubscriberContext.Provider>
  );
}
